import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import CardItemDTO from '@src/adapters/dto/card/CardItemDTO';
import useSaveCardItemDetailsLazy from '@src/adapters/hooks/card/useSaveCardItemDetails';
import useDeleteCardItemLazy from '@src/adapters/hooks/card/useDeleteCardItem';
import { CardModificationDTO } from '@src/adapters/dto/card/CardModificationDTO';

interface Props {
  cardId: string;
  item: CardItemDTO;
  open: boolean;
  onCancel(): void;
  onClose(cardItem: CardItemDTO, modification: CardModificationDTO): void;
  onDelete(id: string, modification: CardModificationDTO): void;
}

function CardItemDetails({
  cardId,
  item,
  open,
  onCancel,
  onClose,
  onDelete,
}: Props) {
  const [defaultValue, setDefaultValue] = useState('');
  const { execute: save } = useSaveCardItemDetailsLazy();
  const { execute: deleteItem } = useDeleteCardItemLazy();
  const { control, handleSubmit, setValue } = useForm<CardItemDTO>();

  const onSubmit = async (data: CardItemDTO) => {
    const result = await save({
      id: data.id,
      isLoan: data.isLoan,
      note: data.note,
      qtyOnHand: data.qtyOnHand,
      qtyOpen: data.qtyOpen,
      cardId,
    });

    onClose(result.cardItem, result.modification);
  };

  useEffect(() => {
    setValue('id', item.id);
    setValue('qtyOpen', item.qtyOpen);
    setValue('qtyOnHand', item.qtyOnHand);
    setValue('isLoan', item.isLoan);
    if (item.note) {
      setValue('note', item.note);
      setDefaultValue(item.note);
    } else {
      setValue('note', '');
      setDefaultValue('');
    }
  }, [item, setValue]);

  const handleDeleteItem = async () => {
    const result = await deleteItem({
      cardId,
      itemId: item.id,
    });
    onDelete(item.id, result.modification);
  };

  return (
    <Popover
      open={open}
      aria-labelledby="Note"
      aria-describedby="note"
      disableEnforceFocus
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}>
      <DialogTitle
        id="alert-dialog-title"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
        }}>
        <Typography variant="h6">Item Details</Typography>
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}>
          <Box sx={(theme) => ({ padding: theme.spacing(2), width: '100%' })}>
            <TextField
              label="Item"
              fullWidth
              disabled
              value={item.inventory.name}
            />
          </Box>
          <Box
            sx={(theme) => ({
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'left',
              width: '100%',
              padding: theme.spacing(2),
            })}>
            <Controller
              name="qtyOpen"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  sx={{ width: '48%' }}
                  label="Open"
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    min: 0,
                  }}
                />
              )}
            />
            <Controller
              name="qtyOnHand"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  sx={{ width: '48%' }}
                  label="On Hand"
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    min: 0,
                  }}
                />
              )}
            />
          </Box>
          <Box sx={(theme) => ({ padding: theme.spacing(2), width: '100%' })}>
            <Controller
              name="note"
              control={control}
              defaultValue={defaultValue}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Notes"
                  multiline
                  fullWidth
                  disabled
                  rows={10}
                  sx={(theme) => ({
                    height: '250px',
                  })}
                />
              )}
            />
          </Box>
          <Box sx={(theme) => ({ padding: theme.spacing(2), width: '100%' })}>
            <Controller
              name="isLoan"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value} />}
                  label="Mark as Loan"
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ flex: '1', marginRight: '8px' }}>
          <Button
            onClick={() => onCancel()}
            color="primary"
            variant="outlined"
            sx={{ flex: '1', marginLeft: '8px' }}>
            Cancel
          </Button>
          <Button
            onClick={() => handleDeleteItem()}
            color="error"
            variant="contained"
            sx={{ flex: '1', marginLeft: '8px' }}>
            Remove
          </Button>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            sx={{ flex: '1', marginLeft: '8px' }}>
            Update
          </Button>
        </DialogActions>
      </form>
    </Popover>
  );
}

export default CardItemDetails;
